import { type File, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import {
  useContext,
  useEffect,
  useState,
  useMemo,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
} from 'react';
import { toast } from 'sonner';
import { VList } from 'virtua';

import { Icons } from '@/assets/icons/icons';
import { HideCanvasAttachmentsContext } from '@/components/blocks/Message/attachments-context';
import { ImagePreviewModal } from '@/components/modals/ImagePreviewModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button, IconButton } from '@/components/ui';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArtifactCard,
  ArtifactCardSkeleton,
  FilePreviewContent,
  HtmlPreviewModeToggle,
  PendingArtifactCard,
  PdfPreviewFrame,
  type HtmlPreviewMode,
} from '@/features/artifacts';
import type { PendingSandboxFile } from '@/features/chat/chat.hooks';
import { useOpenFilePreview } from '@/features/chat/file-preview-host';
import { logError } from '@/lib/errorLogging';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';
import { userStore } from '@/stores/user.store';
import {
  MAX_HTML_PREVIEW_BYTES,
  MAX_TEXT_PREVIEW_BYTES,
} from '@/utils/constants';
import {
  MAX_ZIP_DOWNLOAD_BYTES,
  ZipDownloadTooLargeError,
  downloadFileFromUrl,
  downloadFilesAsZip,
  downloadFileWithFeedback,
} from '@/utils/file-io';
import {
  formatFileSize,
  truncateFilename,
  getFriendlyMimeName,
  isCanvasFile,
  isPastedTextFile,
  isTextPreviewable,
  isHtmlFile,
  isPdfFile,
  getFileExtension,
  isImageFile,
  getDisplayFilename,
  imagesZipFilename,
  selectAdaptiveImageVariants,
} from '@/utils/files';

const compactFormatter = Intl.NumberFormat('en', { notation: 'compact' });
const formatCompactNumber = (n: number) => compactFormatter.format(n);
const GENERATED_IMAGE_GALLERY_HEIGHT_CLASS = 'h-80';
const GENERATED_IMAGE_THUMBNAIL_SIZE_CLASS =
  'h-[5rem] w-[5rem] min-h-[5rem] min-w-[5rem] max-h-[5rem] max-w-[5rem]';
const ASSISTANT_ATTACHMENT_SIZE_CLASS =
  'h-[20rem] w-[20rem] min-h-[20rem] min-w-[20rem] max-h-[20rem] max-w-[20rem]';
const USER_ATTACHMENT_SIZE_CLASS =
  'h-[7rem] w-[7rem] min-h-[7rem] min-w-[7rem] max-h-[7rem] max-w-[7rem]';
// Keep user attachment rows fixed-height so loading/resolved states don't shift chat layout
const USER_ATTACHMENT_ROW_HEIGHT_CLASS = 'h-[7rem] min-h-[7rem] max-h-[7rem]';
const USER_TEXT_ATTACHMENT_CARD_HEIGHT_CLASS =
  'h-[7rem] min-h-[7rem] max-h-[7rem]';
const ASSISTANT_PENDING_ATTACHMENT_SIZE_CLASS = `${ASSISTANT_ATTACHMENT_SIZE_CLASS} rounded-lg`;
const USER_PENDING_ATTACHMENT_SIZE_CLASS = `${USER_ATTACHMENT_SIZE_CLASS} rounded-xl`;

async function downloadImagesAsZip(
  filesStore: {
    getFileUrl(file: File): Promise<string>;
  },
  files: File[]
) {
  try {
    await downloadFilesAsZip(filesStore, files, imagesZipFilename(new Date()));
  } catch (error) {
    if (error instanceof ZipDownloadTooLargeError) {
      toast.error(
        `These images are over ${formatFileSize(MAX_ZIP_DOWNLOAD_BYTES)} together. Download them one at a time.`
      );
      return;
    }
    logError(error, 'Failed to download all images', {
      context: 'Attachments',
      metadata: { fileIds: files.map((file) => file.id) },
    });
    toast.error("We couldn't prepare the image download. Please try again.");
  }
}

interface IAttachments {
  fileIds: string[];
  isAssistant: boolean;
  /**
   * Files the agent committed to surfacing whose metadata has not synced yet, used to name the
   * placeholder card while the ids resolve.
   */
  pendingFiles?: PendingSandboxFile[];
  /**
   * Called once when an image successfully loads and renders.
   * Fired after the file URL is retrieved from the store.
   * @param fileId - The ID of the file that loaded
   * @param loadTimeMs - Time taken to load the image in milliseconds
   */
  onImageLoaded?: (fileId: string, loadTimeMs: number) => void;
  /**
   * Called once when an image fails to load.
   * Fired if the file URL retrieval fails or the image element fails to render.
   * @param fileId - The ID of the file that failed to load
   * @param error - The error that occurred during loading
   * @param timeToFailureMs - Time until failure in milliseconds
   */
  onImageError?: (
    fileId: string,
    error: Error,
    timeToFailureMs: number
  ) => void;
  /**
   * Called each time a user clicks to view an image in full-screen modal.
   * (ie possibly multiple times)
   * @param fileId - The ID of the file being viewed
   */
  onImageViewed?: (fileId: string) => void;
  /**
   * Called each time a user successfully downloads an image.
   * @param fileId - The ID of the file being downloaded
   */
  onImageDownloaded?: (fileId: string) => void;
}

function useAttachmentImageUrl({
  file,
  onImageError,
  onImageLoaded,
}: {
  file: File;
  onImageError?: (
    fileId: string,
    error: Error,
    timeToFailureMs: number
  ) => void;
  onImageLoaded?: (fileId: string, loadTimeMs: number) => void;
}) {
  const { files: filesStore } = useStores();
  const [imageSrc, setImageSrc] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;

    const loadImage = async () => {
      setIsLoading(true);
      setLoadError(false);

      try {
        const url = await filesStore.getFileUrl(file);
        if (!cancelled) {
          const timeMs = Date.now() - startTime;
          setImageSrc(url);
          setIsLoading(false);
          onImageLoaded?.(file.id, timeMs);
        }
      } catch (err) {
        const error = err as Error;
        const timeMs = Date.now() - startTime;
        console.error('[AttachmentImage] Failed to load image URL:', {
          fileId: file.id,
          error,
          errorMessage: error.message,
          errorStack: error.stack,
        });
        if (!cancelled) {
          toast.error('Failed to load generated image. Please try again.');
          setImageSrc('');
          setIsLoading(false);
          setLoadError(true);
          onImageError?.(file.id, error, timeMs);
        }
      }
    };

    loadImage();

    return () => {
      cancelled = true;
    };
  }, [filesStore, file, startTime, onImageLoaded, onImageError]);

  return { imageSrc, isLoading, loadError, setLoadError, startTime };
}

export const Attachments = observer(
  ({
    fileIds,
    isAssistant,
    pendingFiles = [],
    onImageLoaded,
    onImageError,
    onImageViewed,
    onImageDownloaded,
  }: IAttachments) => {
    const { files: filesStore } = useStores();
    const hideCanvasAttachments = useContext(HideCanvasAttachmentsContext);
    const openFilePreview = useOpenFilePreview();

    useEffect(() => {
      if (fileIds.length === 0) return;
      const missingIds = fileIds.filter((id) => !filesStore.getById(id));
      if (missingIds.length > 0) {
        void filesStore.hydrateByIds(missingIds);
      }
    }, [fileIds, filesStore]);

    // No files: skip the fixed-height row so it doesn't reserve an empty gap.
    if (fileIds.length === 0) return null;

    const attachmentEntries = fileIds
      .map((fileId) => ({
        fileId,
        file: filesStore.getById(fileId),
      }))
      .filter(
        ({ file }) =>
          !hideCanvasAttachments ||
          !isAssistant ||
          !file ||
          !isCanvasFile(file.contentType, file.filename)
      )
      .map((entry) => ({
        ...entry,
        filename:
          entry.file && isImageFile(entry.file.contentType, entry.file.filename)
            ? entry.file.filename
            : `unpaired:${entry.fileId}`,
      }));
    const resolved = isAssistant
      ? selectAdaptiveImageVariants(attachmentEntries, uiStore.theme)
      : attachmentEntries;
    if (resolved.length === 0) return null;
    const hydratedFiles = resolved
      .map((r) => r.file)
      .filter((f): f is File => f !== undefined);
    const assistantImageFiles = isAssistant
      ? hydratedFiles.filter((file) =>
          isImageFile(file.contentType, file.filename)
        )
      : [];
    const unresolvedFileCount = resolved.filter((entry) => !entry.file).length;
    const allFilesPending = hydratedFiles.length === 0;
    const shouldRenderImageGallery =
      isAssistant &&
      resolved.length > 1 &&
      (allFilesPending ||
        assistantImageFiles.length > 1 ||
        (assistantImageFiles.length > 0 && unresolvedFileCount > 0));

    // User rows fall through so the fixed-height row is reserved while
    // files hydrate; returning null here pops the row in later and shoves
    // the chat layout down.
    if (allFilesPending && !shouldRenderImageGallery && isAssistant) {
      return pendingFiles.length === resolved.length ? (
        <PendingAssistantFiles files={pendingFiles} />
      ) : (
        <AttachmentSkeletons count={resolved.length} />
      );
    }

    return (
      <YStack className={cn('w-full gap-1.5', isAssistant ? 'mt-2' : '')}>
        <div
          className={cn(
            'w-full',
            !isAssistant && 'overflow-x-auto overflow-y-hidden scrollbar-hover'
          )}
        >
          <div
            className={cn(
              'flex gap-2',
              isAssistant
                ? 'w-full flex-col items-start'
                : cn(
                    'w-max min-w-full flex-row flex-nowrap items-end justify-end',
                    USER_ATTACHMENT_ROW_HEIGHT_CLASS
                  )
            )}
          >
            {shouldRenderImageGallery && assistantImageFiles.length > 0 ? (
              <GeneratedImageGallery
                files={assistantImageFiles}
                pendingCount={unresolvedFileCount}
                onDownloadAll={() =>
                  downloadImagesAsZip(filesStore, assistantImageFiles)
                }
                onImageLoaded={onImageLoaded}
                onImageError={onImageError}
                onImageViewed={onImageViewed}
                onImageDownloaded={onImageDownloaded}
              />
            ) : shouldRenderImageGallery ? (
              <GeneratedImageGallerySkeleton count={resolved.length} />
            ) : null}
            {resolved.map(({ fileId, file }) => {
              // Still-hydrating file in a partially-loaded set: keep a placeholder slot.
              if (!file) {
                if (shouldRenderImageGallery) return null;

                return (
                  <PendingAttachmentSlot
                    key={fileId}
                    isAssistant={isAssistant}
                  />
                );
              }
              const isImage = isImageFile(file.contentType, file.filename);
              if (isImage) {
                if (shouldRenderImageGallery && isAssistant) return null;

                return (
                  <AttachmentImage
                    key={file.id}
                    file={file}
                    isAssistant={isAssistant}
                    onImageLoaded={onImageLoaded}
                    onImageError={onImageError}
                    onImageViewed={onImageViewed}
                    onImageDownloaded={onImageDownloaded}
                  />
                );
              }

              if (isAssistant) {
                return (
                  <ArtifactCard
                    key={file.id}
                    file={file}
                    onOpen={openFilePreview}
                  />
                );
              }

              if (isHtmlFile(file.contentType, file.filename))
                return <AttachmentHtmlFile key={file.id} file={file} />;
              if (isPdfFile(file.contentType, file.filename))
                return <AttachmentPdfFile key={file.id} file={file} />;
              if (isTextPreviewable(file.contentType, file.filename))
                return <AttachmentTextFile key={file.id} file={file} />;

              return <AttachmentItem key={file.id} file={file} />;
            })}
          </div>
        </div>
      </YStack>
    );
  }
);

export const AttachmentSkeletons = ({ count }: { count: number }) => {
  if (count <= 0) return null;
  return (
    <YStack className="flex w-full gap-2 mt-2 items-start">
      {Array.from({ length: count }, (_, i) => (
        <ArtifactCardSkeleton key={i} />
      ))}
    </YStack>
  );
};

export const PendingAssistantFiles = ({
  files,
}: {
  files: PendingSandboxFile[];
}) => {
  if (files.length === 0) return null;
  return (
    <YStack className="flex w-full gap-2 mt-2 items-start">
      {files.map((file) => (
        <PendingArtifactCard
          key={file.name}
          name={file.name}
          isPreparing={file.state === 'preparing'}
        />
      ))}
    </YStack>
  );
};

interface AttachmentItemProps {
  file: File;
}

function PendingAttachmentSlot({ isAssistant }: { isAssistant: boolean }) {
  return (
    <Skeleton
      variant="low-emphasis"
      className={cn(
        isAssistant
          ? ASSISTANT_PENDING_ATTACHMENT_SIZE_CLASS
          : USER_PENDING_ATTACHMENT_SIZE_CLASS,
        'shrink-0'
      )}
    />
  );
}

function AttachmentImageFrame({
  children,
  isAssistant,
}: {
  children: ReactNode;
  isAssistant: boolean;
}) {
  return (
    <div
      className={cn(
        'relative shrink-0 overflow-hidden bg-surface-active smoothing-default',
        isAssistant
          ? `${ASSISTANT_ATTACHMENT_SIZE_CLASS} rounded-lg`
          : `${USER_ATTACHMENT_SIZE_CLASS} rounded-xl`
      )}
    >
      {children}
    </div>
  );
}

export const AttachmentItem = observer(({ file }: AttachmentItemProps) => {
  const displayName = getDisplayFilename(file.filename);
  return (
    <XStack
      className={cn(
        'bg-surface-active',
        'gap-3 items-start',
        'pl-2.5 pr-4 py-1.5',
        'max-w-56',
        'shrink-0',
        'border border-border-strong',
        'rounded-xl smoothing-default',
        'overflow-hidden'
      )}
    >
      <YStack className="items-center justify-center size-10 rounded-md bg-surface-hover">
        <Icons.file className="size-5 text-gray-12" />
      </YStack>
      <YStack className="flex-1 min-w-0">
        <div className="text-sm truncate" title={displayName}>
          {truncateFilename(displayName)}
        </div>
        <div className="text-xs text-foreground-subtle truncate">
          {getFriendlyMimeName(file.contentType, file.filename)}
        </div>
        {file.size && (
          <div className="text-xs text-foreground-muted truncate">
            {formatFileSize(file.size)}
          </div>
        )}
      </YStack>
    </XStack>
  );
});

export const AttachmentTextFile = observer(({ file }: AttachmentItemProps) => {
  const { files: filesStore } = useStores();
  const [textContent, setTextContent] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const isPasted = isPastedTextFile(file.contentType, file.filename);
  const displayName = getDisplayFilename(file.filename);
  const badge = isPasted ? 'PASTED' : getFileExtension(file.filename);
  const dialogTitle = isPasted ? 'Pasted content' : displayName;

  const knownTooLarge = file.size > MAX_TEXT_PREVIEW_BYTES;
  const [isTooLarge, setIsTooLarge] = useState(knownTooLarge);

  const lineCount = useMemo(
    () => (textContent ? textContent.split('\n').length : 0),
    [textContent]
  );

  const lines = useMemo(
    () => (isOpen && !isTooLarge ? textContent.split('\n') : []),
    [textContent, isOpen, isTooLarge]
  );

  useEffect(() => {
    if (knownTooLarge) {
      setIsTooLarge(true);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadContent = async () => {
      try {
        const url = await filesStore.getFileUrl(file);
        if (cancelled) return;

        if (!url) {
          setHasError(true);
          setIsLoading(false);
          return;
        }

        const response = await fetch(url);
        if (cancelled) return;

        if (!response.ok) {
          setHasError(true);
          setIsLoading(false);
          return;
        }

        const contentLength = response.headers.get('content-length');
        if (
          contentLength &&
          parseInt(contentLength, 10) > MAX_TEXT_PREVIEW_BYTES
        ) {
          setIsTooLarge(true);
          setIsLoading(false);
          return;
        }

        const text = await response.text();
        if (cancelled) return;

        if (text.length > MAX_TEXT_PREVIEW_BYTES) {
          setIsTooLarge(true);
          setIsLoading(false);
          return;
        }

        setTextContent(text);
        setIsLoading(false);
      } catch (error) {
        if (!cancelled) {
          logError(error, 'Failed to load text file', {
            context: 'AttachmentTextFile',
            metadata: { fileId: file.id },
          });
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    loadContent();
    return () => {
      cancelled = true;
    };
  }, [file, filesStore, knownTooLarge]);

  return (
    <>
      <YStack
        onClick={() => !isLoading && setIsOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
          if ((e.key === 'Enter' || e.key === ' ') && !isLoading) {
            e.preventDefault();
            setIsOpen(true);
          }
        }}
        aria-label={`View ${isPasted ? 'pasted content' : displayName}`}
        className={cn(
          'group/text-preview-card',
          'bg-surface-active',
          'px-3.5 py-3',
          'w-44',
          USER_TEXT_ATTACHMENT_CARD_HEIGHT_CLASS,
          'shrink-0',
          'relative',
          'rounded-xl smoothing-default',
          'overflow-hidden',
          'select-none',
          'cursor-default'
        )}
      >
        <div className="flex-1 overflow-hidden mask-linear mask-from-100 mask-via-100 mask-to-0 mask-point-via-[calc(100%-16px)]">
          {isLoading ? (
            <YStack className="gap-1.5">
              <Skeleton variant="low-emphasis" className="h-3 w-full" />
              <Skeleton variant="low-emphasis" className="h-3 w-3/5" />
            </YStack>
          ) : hasError ? (
            <p className="text-xs text-foreground-muted leading-tight">
              Failed to load preview
            </p>
          ) : (
            <p className="text-xs text-foreground-muted group-hover/text-preview-card:text-foreground leading-tight transition-colors">
              {textContent.slice(0, 200)}
            </p>
          )}
        </div>
        <XStack className="items-center gap-2 mt-2">
          <span className="text-xs font-medium text-foreground-subtle border border-border-strongest px-1.5 py-0.5 rounded">
            {badge}
          </span>
          {lineCount > 0 && (
            <span className="ml-auto text-xs tabular-nums text-foreground-muted truncate">
              {`${formatCompactNumber(lineCount)} line${lineCount > 1 ? 's' : ''}`}
            </span>
          )}
        </XStack>
      </YStack>

      <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Content className="max-w-2xl max-h-[80vh] flex flex-col">
          <Dialog.Header>
            <Dialog.Title className="truncate">
              {truncateFilename(dialogTitle, 40)}
            </Dialog.Title>
            <Dialog.Description>
              {formatFileSize(file.size ?? 0)}
              {!isTooLarge &&
                ` · ${lines.length} ${lines.length === 1 ? 'line' : 'lines'}`}
              {isPasted && ' · Formatting may be inconsistent from source'}
            </Dialog.Description>
          </Dialog.Header>
          <div className="h-[60vh]">
            {isTooLarge || hasError ? (
              <div className="h-full flex items-center justify-center text-sm text-foreground-muted rounded-md border border-border-strong bg-surface">
                {hasError ? 'Failed to load file' : 'File too large to preview'}
              </div>
            ) : (
              <VList
                className={cn(
                  'h-full text-sm font-mono p-4 rounded-md border border-border-strong bg-surface',
                  'scrollbar-hover',
                  'mask-linear mask-from-100 mask-via-100 mask-to-0 mask-point-via-[95%]',
                  'cursor-text select-text'
                )}
              >
                {lines.map((line, i) => (
                  <div
                    key={i}
                    className="text-foreground whitespace-pre-wrap break-words min-h-[1.5em]"
                  >
                    {line || '\u00A0'}
                  </div>
                ))}
              </VList>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
});

/** Clickable card chrome shared by the HTML and PDF preview attachments. */
const PreviewCard = ({
  file,
  badge,
  hint,
  disabled,
  onOpen,
}: {
  file: File;
  badge: string;
  hint: string;
  disabled: boolean;
  onOpen: () => void;
}) => {
  const displayName = getDisplayFilename(file.filename);
  return (
    <XStack
      onClick={() => !disabled && onOpen()}
      role="button"
      tabIndex={0}
      onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label={`Preview ${displayName}`}
      className={cn(
        'bg-surface-active',
        'gap-3 items-center',
        'pl-2.5 pr-4 py-1.5',
        'max-w-56',
        'shrink-0',
        'border border-border-strong',
        'rounded-xl smoothing-default',
        'overflow-hidden',
        disabled ? 'opacity-60' : 'cursor-default hover:bg-surface-hover'
      )}
    >
      <YStack className="items-center justify-center size-10 rounded-md bg-surface-hover">
        <span className="text-xs font-medium text-foreground-subtle">
          {badge}
        </span>
      </YStack>
      <YStack className="flex-1 min-w-0">
        <div className="text-sm truncate" title={displayName}>
          {truncateFilename(displayName)}
        </div>
        <div className="text-xs text-foreground-subtle truncate">{hint}</div>
        {file.size ? (
          <div className="text-xs text-foreground-muted truncate">
            {formatFileSize(file.size)}
          </div>
        ) : null}
      </YStack>
    </XStack>
  );
};

export const AttachmentHtmlFile = observer(({ file }: AttachmentItemProps) => {
  const { files: filesStore } = useStores();
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<HtmlPreviewMode>('preview');
  const [reloadKey, setReloadKey] = useState(0);

  const displayName = getDisplayFilename(file.filename);
  const isTooLarge = file.size > MAX_HTML_PREVIEW_BYTES;

  // Per-open state: every open starts back on the rendered preview.
  const openDialog = () => {
    setViewMode('preview');
    setIsOpen(true);
  };

  const handleDownload = () =>
    void downloadFileWithFeedback(filesStore, file, 'AttachmentItem');

  return (
    <>
      <PreviewCard
        file={file}
        badge="HTML"
        hint={isTooLarge ? 'Too large to preview' : 'Click to preview'}
        disabled={isTooLarge}
        onOpen={openDialog}
      />
      <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Content className="max-w-4xl max-h-[85vh] flex flex-col">
          <Dialog.Header>
            {/* pr-10 keeps the controls clear of the absolute close button. */}
            <XStack className="items-center gap-2 pr-10">
              <Dialog.Title className="truncate flex-1">
                {truncateFilename(displayName, 40)}
              </Dialog.Title>
              {userStore.isInternalUser && (
                <HtmlPreviewModeToggle
                  mode={viewMode}
                  onModeChange={setViewMode}
                />
              )}
              <IconButton
                size="sm"
                icon={Icons.refresh}
                tooltip="Reload"
                aria-label="Reload preview"
                onClick={() => setReloadKey((key) => key + 1)}
              />
              <IconButton
                size="sm"
                icon={Icons.download}
                tooltip="Download"
                aria-label="Download file"
                onClick={handleDownload}
              />
            </XStack>
            <Dialog.Description>
              {formatFileSize(file.size ?? 0)} · Rendered in a sandboxed frame
            </Dialog.Description>
          </Dialog.Header>
          {/* Radix unmounts closed dialogs, so the content only downloads on open. */}
          <div
            className="h-[70vh] overflow-hidden rounded-md border border-border-strong"
            key={reloadKey}
          >
            <FilePreviewContent file={file} htmlViewMode={viewMode} />
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
});

export const AttachmentPdfFile = observer(({ file }: AttachmentItemProps) => {
  const { files: filesStore } = useStores();
  const [url, setUrl] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const displayName = getDisplayFilename(file.filename);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const fileUrl = await filesStore.getFileUrl(file);
        if (cancelled) return;
        if (!fileUrl) {
          setHasError(true);
          setIsLoading(false);
          return;
        }
        setUrl(fileUrl);
        setIsLoading(false);
      } catch (error) {
        if (!cancelled) {
          logError(error, 'Failed to load PDF file', {
            context: 'AttachmentPdfFile',
            metadata: { fileId: file.id },
          });
          setHasError(true);
          setIsLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [file, filesStore]);

  const hint = hasError
    ? 'Failed to load'
    : isLoading
      ? 'Loading…'
      : 'Click to preview';

  return (
    <>
      <PreviewCard
        file={file}
        badge="PDF"
        hint={hint}
        disabled={isLoading || hasError}
        onOpen={() => setIsOpen(true)}
      />
      <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Content className="max-w-4xl max-h-[85vh] flex flex-col">
          <Dialog.Header>
            <Dialog.Title className="truncate">
              {truncateFilename(displayName, 40)}
            </Dialog.Title>
            <Dialog.Description>
              {formatFileSize(file.size ?? 0)}
            </Dialog.Description>
          </Dialog.Header>
          <div className="h-[70vh]">
            <PdfPreviewFrame
              title={`Preview ${displayName}`}
              url={url}
              className="h-full w-full rounded-md border border-border-strong bg-white"
            />
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
});

interface AttachmentImageProps {
  file: File;
  isAssistant: boolean;
  onImageLoaded?: (fileId: string, loadTimeMs: number) => void;
  onImageError?: (
    fileId: string,
    error: Error,
    timeToFailureMs: number
  ) => void;
  onImageViewed?: (fileId: string) => void;
  onImageDownloaded?: (fileId: string) => void;
}

interface GeneratedImageGalleryProps {
  files: File[];
  /** Attachments still hydrating; they hold a thumbnail slot and block "Download all". */
  pendingCount: number;
  onDownloadAll: () => Promise<void>;
  onImageLoaded?: (fileId: string, loadTimeMs: number) => void;
  onImageError?: (
    fileId: string,
    error: Error,
    timeToFailureMs: number
  ) => void;
  onImageViewed?: (fileId: string) => void;
  onImageDownloaded?: (fileId: string) => void;
}

const GeneratedImageGallery = observer(function GeneratedImageGallery({
  files,
  pendingCount,
  onDownloadAll,
  onImageLoaded,
  onImageError,
  onImageViewed,
  onImageDownloaded,
}: GeneratedImageGalleryProps) {
  const [selectedFileId, setSelectedFileId] = useState(files[0]?.id ?? '');
  const [isDownloading, setIsDownloading] = useState(false);
  const selectedFile =
    files.find((file) => file.id === selectedFileId) ?? files[0];

  const handleDownloadAll = async () => {
    setIsDownloading(true);
    try {
      await onDownloadAll();
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (files.some((file) => file.id === selectedFileId)) return;
    setSelectedFileId(files[0]?.id ?? '');
  }, [files, selectedFileId]);

  if (!selectedFile) return null;

  return (
    <XStack
      className={cn(
        GENERATED_IMAGE_GALLERY_HEIGHT_CLASS,
        'w-full max-w-[44rem] items-stretch gap-3'
      )}
    >
      <YStack className={cn(ASSISTANT_ATTACHMENT_SIZE_CLASS, 'shrink-0 gap-2')}>
        <GeneratedImageGalleryMain
          file={selectedFile}
          onImageLoaded={onImageLoaded}
          onImageError={onImageError}
          onImageViewed={onImageViewed}
          onImageDownloaded={onImageDownloaded}
        />
        <Button
          className="w-full justify-center text-foreground-subtle hover:text-foreground"
          disabled={pendingCount > 0}
          isLoading={isDownloading}
          loadingText="Preparing…"
          onClick={() => void handleDownloadAll()}
          size="sm"
          variant="ghost"
        >
          <XStack className="items-center gap-1.5">
            <Icons.download className="size-3.5" />
            Download all ({files.length + pendingCount})
          </XStack>
        </Button>
      </YStack>
      <YStack
        className={cn(
          GENERATED_IMAGE_GALLERY_HEIGHT_CLASS,
          'w-24 shrink-0 gap-2 overflow-y-auto pr-1 scrollbar-hover'
        )}
        aria-label="Generated image options"
      >
        {files.map((file) => (
          <GeneratedImageThumbnail
            key={file.id}
            file={file}
            selected={file.id === selectedFile.id}
            onSelect={() => setSelectedFileId(file.id)}
          />
        ))}
        {Array.from({
          length: pendingCount,
        }).map((_, index) => (
          <Skeleton
            key={`pending-${index}`}
            variant="low-emphasis"
            className={cn(
              GENERATED_IMAGE_THUMBNAIL_SIZE_CLASS,
              'shrink-0 rounded-lg'
            )}
          />
        ))}
      </YStack>
    </XStack>
  );
});

function GeneratedImageGallerySkeleton({ count }: { count: number }) {
  return (
    <XStack
      className={cn(
        GENERATED_IMAGE_GALLERY_HEIGHT_CLASS,
        'w-full max-w-[44rem] items-stretch gap-3'
      )}
    >
      <YStack className={cn(ASSISTANT_ATTACHMENT_SIZE_CLASS, 'shrink-0 gap-2')}>
        <Skeleton
          variant="low-emphasis"
          className="min-h-0 w-80 flex-1 rounded-lg"
        />
        <Button
          className="w-full justify-center text-foreground-subtle hover:text-foreground"
          disabled
          size="sm"
          variant="ghost"
        >
          <XStack className="items-center gap-1.5">
            <Icons.download className="size-3.5" />
            Download all ({count})
          </XStack>
        </Button>
      </YStack>
      <YStack
        className={cn(
          GENERATED_IMAGE_GALLERY_HEIGHT_CLASS,
          'w-24 shrink-0 gap-2 overflow-hidden pr-1'
        )}
      >
        {Array.from({ length: count }).map((_, index) => (
          <Skeleton
            key={index}
            variant="low-emphasis"
            className={cn(
              GENERATED_IMAGE_THUMBNAIL_SIZE_CLASS,
              'shrink-0 rounded-lg'
            )}
          />
        ))}
      </YStack>
    </XStack>
  );
}

function GeneratedImageGalleryMain({
  file,
  onImageDownloaded,
  onImageError,
  onImageLoaded,
  onImageViewed,
}: Omit<AttachmentImageProps, 'isAssistant'>) {
  const { imageSrc, isLoading, loadError, setLoadError, startTime } =
    useAttachmentImageUrl({ file, onImageError, onImageLoaded });

  const downloadImage = useCallback(async () => {
    try {
      await downloadFileFromUrl(imageSrc, file.filename);
      onImageDownloaded?.(file.id);
    } catch (error) {
      logError(error, 'Failed to download image', {
        context: 'AttachmentImage',
        metadata: { imageSrc, filename: file.filename },
      });
      onImageError?.(
        file.id,
        new Error('Download failed during fetching'),
        Date.now() - startTime
      );
    }
  }, [
    file.filename,
    file.id,
    imageSrc,
    onImageDownloaded,
    onImageError,
    startTime,
  ]);

  const showImagePreview = useCallback(() => {
    ModalsManager.show(ImagePreviewModal, {
      url: imageSrc,
      filename: file.filename,
      onDownload: downloadImage,
    });
  }, [downloadImage, file.filename, imageSrc]);

  return (
    <div className="relative min-h-0 w-80 flex-1 overflow-hidden rounded-lg">
      {loadError ? (
        <div
          className="flex size-full items-center justify-center bg-surface-active"
          role="alert"
          aria-live="assertive"
          aria-label="Image loading failed"
        >
          <Icons.file className="size-8 text-foreground-muted" />
        </div>
      ) : isLoading || !imageSrc ? (
        <Skeleton variant="low-emphasis" className="size-full rounded-lg" />
      ) : (
        <img
          src={imageSrc}
          alt={file.filename}
          className="size-full object-cover cursor-default transition-opacity hover:opacity-90"
          onClick={() => {
            showImagePreview();
            onImageViewed?.(file.id);
          }}
          onError={(event) => {
            console.error('[AttachmentImage] Image load error (assistant):', {
              fileId: file.id,
              src: imageSrc,
              error: event,
            });
            setLoadError(true);
            onImageError?.(
              file.id,
              new Error('Image element failed to load'),
              Date.now() - startTime
            );
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(event: KeyboardEvent<HTMLImageElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              showImagePreview();
              onImageViewed?.(file.id);
            }
          }}
          aria-label={`Click to view full size image: ${file.filename}`}
        />
      )}
    </div>
  );
}

function GeneratedImageThumbnail({
  file,
  onSelect,
  selected,
}: {
  file: File;
  onSelect: () => void;
  selected: boolean;
}) {
  const { imageSrc, isLoading, loadError } = useAttachmentImageUrl({ file });

  return (
    <button
      type="button"
      aria-label={`Show generated image ${file.filename}`}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onSelect();
      }}
      className={cn(
        GENERATED_IMAGE_THUMBNAIL_SIZE_CLASS,
        'shrink-0 overflow-hidden rounded-lg border smoothing-default',
        selected ? 'border-accent-default' : 'border-transparent'
      )}
    >
      {isLoading || !imageSrc ? (
        <Skeleton variant="low-emphasis" className="size-full rounded-lg" />
      ) : loadError ? (
        <div className="flex size-full items-center justify-center bg-surface-active">
          <Icons.file className="size-4 text-foreground-muted" />
        </div>
      ) : (
        <img
          src={imageSrc}
          alt=""
          className="size-full object-cover"
          draggable={false}
        />
      )}
    </button>
  );
}

export const AttachmentImage = observer(
  ({
    file,
    isAssistant,
    onImageLoaded,
    onImageError,
    onImageViewed,
    onImageDownloaded,
  }: AttachmentImageProps) => {
    const { imageSrc, isLoading, loadError, setLoadError, startTime } =
      useAttachmentImageUrl({ file, onImageError, onImageLoaded });

    const downloadImage = useCallback(async () => {
      try {
        await downloadFileFromUrl(imageSrc, file.filename);
        onImageDownloaded?.(file.id);
      } catch (error) {
        logError(error, 'Failed to download image', {
          context: 'AttachmentImage',
          metadata: { imageSrc, filename: file.filename },
        });
        onImageError?.(
          file.id,
          new Error('Download failed during fetching'),
          Date.now() - startTime
        );
      }
    }, [
      imageSrc,
      file.filename,
      file.id,
      onImageDownloaded,
      onImageError,
      startTime,
    ]);

    const showImagePreview = useCallback(() => {
      ModalsManager.show(ImagePreviewModal, {
        url: imageSrc,
        filename: file.filename,
        onDownload: downloadImage,
      });
    }, [imageSrc, file.filename, downloadImage]);

    if (loadError) {
      return (
        <AttachmentImageFrame isAssistant={isAssistant}>
          <ImageErrorPlaceholder isAssistant={isAssistant} />
        </AttachmentImageFrame>
      );
    }

    if (isLoading || imageSrc === '') {
      return (
        <AttachmentImageFrame isAssistant={isAssistant}>
          <Skeleton variant="low-emphasis" className="size-full" />
        </AttachmentImageFrame>
      );
    }

    if (!isAssistant) {
      return (
        <AttachmentImageFrame isAssistant={false}>
          <img
            src={imageSrc}
            alt={file.filename}
            className="size-full object-cover transition-opacity hover:opacity-90"
            onClick={() => {
              showImagePreview();
              onImageViewed?.(file.id);
            }}
            onError={(e) => {
              console.error('[AttachmentImage] Image load error (user):', {
                fileId: file.id,
                src: imageSrc,
                error: e,
              });
              setLoadError(true);
              onImageError?.(
                file.id,
                new Error('Image element failed to load'),
                Date.now() - startTime
              );
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e: KeyboardEvent<HTMLImageElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                showImagePreview();
                onImageViewed?.(file.id);
              }
            }}
            aria-label={`Click to view full size image: ${file.filename}`}
          />
        </AttachmentImageFrame>
      );
    }

    return (
      <>
        <AttachmentImageFrame isAssistant>
          <div className="group/img size-full">
            <img
              src={imageSrc}
              alt={file.filename}
              className="size-full object-cover cursor-default transition-opacity hover:opacity-90"
              onClick={() => {
                showImagePreview();
                onImageViewed?.(file.id);
              }}
              onError={(e) => {
                console.error(
                  '[AttachmentImage] Image load error (assistant):',
                  {
                    fileId: file.id,
                    src: imageSrc,
                    error: e,
                  }
                );
                setLoadError(true);
                onImageError?.(
                  file.id,
                  new Error('Image element failed to load'),
                  Date.now() - startTime
                );
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e: KeyboardEvent<HTMLImageElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  showImagePreview();
                  onImageViewed?.(file.id);
                }
              }}
              aria-label={`Click to view full size image: ${file.filename}`}
            />
            <div className="absolute top-2 right-2">
              <IconButton
                size="sm"
                className="opacity-0 touch:opacity-100 group-hover/img:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]/img:opacity-100 transition-opacity border border-border-strong bg-surface rounded-lg smoothing-default text-foreground-subtle hover:text-foreground"
                tabIndex={0}
                icon={Icons.download}
                tooltip="Download image"
                aria-label="Download image"
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  downloadImage();
                }}
              />
            </div>
          </div>
        </AttachmentImageFrame>

        {/* Modal is shown via ModalsManager in the click/keydown handlers */}
      </>
    );
  }
);

const ImageErrorPlaceholder = ({ isAssistant }: { isAssistant: boolean }) => (
  <div
    className={cn('flex size-full items-center justify-center', 'relative')}
    role="alert"
    aria-live="assertive"
    aria-label="Image loading failed"
  >
    <div className="flex flex-col items-center gap-2 p-4">
      <Icons.brokenImage
        className={cn(
          'text-foreground-muted',
          isAssistant ? 'size-8' : 'size-4'
        )}
      />
    </div>
  </div>
);
